import type { FeatureFlagsAppName as AppName } from "../../../models/featureFlags";

import { useFeatureFlagsQuery } from "./featureFlags.query";

export const getFeatureFlagAccessError = (key: string) =>
  new Error(
    `Attempted to access feature flag "${key}" but it is not available`,
  );

// #########################
// ## useMaybeFeatureFlag ##
// #########################

export type ParamsOfUseMaybeFeatureFlag = {
  app: AppName;
  key: string;
};
export type ResultOfUseMaybeFeatureFlag = boolean | undefined;
export type UseMaybeFeatureFlag = (
  params: ParamsOfUseMaybeFeatureFlag,
) => ResultOfUseMaybeFeatureFlag;

/**
 * The result from this hook will contain undefined or
 * the boolean value of the feature flag matching `app` and `key`.
 *
 * This hook is guaranteed to contain data or will throw.
 */
export const useMaybeFeatureFlag: UseMaybeFeatureFlag = ({ app, key }) => {
  const { data } = useFeatureFlagsQuery({
    app,
    watch: ["data"],
  });
  return data?.featureFlagsAsBooleansByKey[key];
};

// ####################
// ## useFeatureFlag ##
// ####################

export type ParamsOfUseFeatureFlag = {
  app: AppName;
  key: string;
};
export type ResultOfUseFeatureFlag = boolean;
export type UseFeatureFlag = (
  params: ParamsOfUseFeatureFlag,
) => ResultOfUseFeatureFlag;

/**
 * The result from this hook will contain
 * the boolean value of the feature flag matching `app` and `key`.
 *
 * This hook is guaranteed to contain data or will throw.
 */
export const useFeatureFlag: UseFeatureFlag = ({ app, key }) => {
  const data = useMaybeFeatureFlag({ app, key });
  if (data === undefined) {
    throw getFeatureFlagAccessError(key);
  }
  return data;
};
